// src/pages/KarigarManagement/Karigar/KarigarAvailabilityPage.jsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useKarigarManagement } from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  Clock,
  Briefcase,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { toast } from "sonner";
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  isSameDay,
  isToday,
} from "date-fns";

// Status configuration for consistency
const STATUS_CONFIG = {
  unavailable: {
    bg: "bg-red-100",
    text: "text-red-700",
    ring: "ring-red-200",
    label: "Unavailable",
  },
  atCapacity: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    ring: "ring-yellow-200",
    label: "At Capacity",
  },
  hasJobs: {
    bg: "bg-blue-100",
    text: "text-blue-700",
    ring: "ring-blue-200",
    label: "Has Jobs",
  },
  available: {
    bg: "bg-green-100",
    text: "text-green-700",
    ring: "ring-green-200",
    label: "Available",
  },
};

const KarigarAvailabilityPage = () => {
  const navigate = useNavigate();
  const { karigarId } = useParams();

  const {
    currentKarigar,
    availability,
    loading,
    error,
    getKarigarById,
    getAvailability,
    clearError,
    clearCurrentKarigar,
    clearAvailability,
  } = useKarigarManagement();

  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Get max concurrent jobs
  const maxJobs =
    currentKarigar?.workPreferences?.maxConcurrentJobs ||
    availability?.capacity?.maxConcurrentJobs ||
    5;

  // Fetch karigar details and availability
  useEffect(() => {
    if (karigarId) {
      getKarigarById(karigarId);
      fetchAvailability(new Date());
    }
    return () => {
      clearCurrentKarigar();
      clearAvailability();
    };
  }, [karigarId]);

  // Fetch availability for a given month
  const fetchAvailability = useCallback(
    (date) => {
      const startDate = format(startOfMonth(date), "yyyy-MM-dd");
      const endDate = format(endOfMonth(date), "yyyy-MM-dd");

      getAvailability(karigarId, {
        startDate,
        endDate,
        includeJobs: true,
      });
    },
    [karigarId, getAvailability]
  );

  // Handle month change
  const handleMonthChange = (date) => {
    setSelectedMonth(date);
    setSelectedDate(null);
    fetchAvailability(date);
  };

  // Handle error
  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, clearError]);

  // Get day status from calendar data
  const getDayStatus = useCallback(
    (date) => {
      const dateStr = format(date, "yyyy-MM-dd");
      return availability?.calendar?.find((d) => d.date === dateStr) || null;
    },
    [availability?.calendar]
  );

  // Determine the status type for a day
  const getDayStatusType = useCallback(
    (dayData) => {
      if (!dayData) return null;
      if (dayData.isUnavailable || dayData.status === "UNAVAILABLE")
        return "unavailable";
      if (dayData.jobCount >= (dayData.maxJobs || maxJobs)) return "atCapacity";
      if (dayData.jobCount > 0) return "hasJobs";
      return "available";
    },
    [maxJobs]
  );

  // Custom Day component for the calendar
  const CustomDayContent = ({ date, displayMonth }) => {
    const dayData = getDayStatus(date);
    const statusType = getDayStatusType(dayData);
    const config = statusType ? STATUS_CONFIG[statusType] : null;
    const isSelected = selectedDate && isSameDay(date, selectedDate);
    const isTodayDate = isToday(date);
    const isOutside = date.getMonth() !== displayMonth.getMonth();

    if (isOutside) {
      return (
        <div className="w-full h-14 flex items-center justify-center opacity-30">
          <span className="text-sm text-muted-foreground">
            {date.getDate()}
          </span>
        </div>
      );
    }

    return (
      <button
        onClick={() => setSelectedDate(date)}
        className={`
          w-full h-14 flex flex-col items-center justify-center rounded-lg
          transition-all duration-200 cursor-pointer relative border
          ${config ? config.bg : "bg-white hover:bg-gray-50"}
          ${config ? `border-transparent` : "border-gray-100"}
          ${
            isSelected
              ? "ring-2 ring-primary ring-offset-2 shadow-lg scale-105"
              : ""
          }
          ${isTodayDate && !isSelected ? "ring-2 ring-primary/40" : ""}
          hover:shadow-md hover:scale-[1.02]
        `}
      >
        <span
          className={`
            text-sm font-medium
            ${config ? config.text : "text-gray-700"}
            ${isTodayDate ? "font-bold" : ""}
          `}
        >
          {date.getDate()}
        </span>
        {dayData && dayData.jobCount > 0 && statusType !== "unavailable" && (
          <span
            className={`
              text-[10px] font-semibold leading-none mt-0.5
              ${config?.text || "text-gray-500"}
            `}
          >
            {dayData.jobCount} job{dayData.jobCount > 1 ? "s" : ""}
          </span>
        )}
        {statusType === "unavailable" && (
          <XCircle className="h-3 w-3 mt-0.5 text-red-500" />
        )}
      </button>
    );
  };

  // Get selected day details
  const selectedDayData = selectedDate ? getDayStatus(selectedDate) : null;
  const selectedStatusType = selectedDayData
    ? getDayStatusType(selectedDayData)
    : null;

  if (loading.detail) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Availability Calendar</h1>
            <p className="text-muted-foreground">
              {currentKarigar?.name || availability?.karigar?.name} -{" "}
              {currentKarigar?.karigarCode ||
                availability?.karigar?.karigarCode}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards - Original Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div
                className={`h-10 w-10 rounded-full flex items-center justify-center ${
                  availability?.currentStatus?.isAvailable
                    ? "bg-green-100"
                    : "bg-red-100"
                }`}
              >
                {availability?.currentStatus?.isAvailable ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Status</p>
                <p className="font-semibold">
                  {availability?.currentStatus?.isAvailable
                    ? "Available"
                    : "Unavailable"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Jobs</p>
                <p className="font-semibold">
                  {availability?.capacity?.currentJobs || 0} /{" "}
                  {availability?.capacity?.maxConcurrentJobs || maxJobs}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Capacity Used</p>
                <p className="font-semibold">
                  {(
                    ((availability?.capacity?.currentJobs || 0) /
                      (availability?.capacity?.maxConcurrentJobs || maxJobs)) *
                    100
                  ).toFixed(0)}
                  %
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Next Available</p>
                <p className="font-semibold">
                  {availability?.currentStatus?.nextAvailableDate
                    ? format(
                        new Date(availability.currentStatus.nextAvailableDate),
                        "dd MMM"
                      )
                    : "Now"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{format(selectedMonth, "MMMM yyyy")}</CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleMonthChange(addMonths(selectedMonth, -1))
                  }
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMonthChange(new Date())}
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleMonthChange(addMonths(selectedMonth, 1))}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading.availability ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Custom Calendar Grid */}
                <div className="border rounded-lg overflow-hidden">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 bg-muted/50 border-b">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                      (day) => (
                        <div
                          key={day}
                          className="py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider"
                        >
                          {day}
                        </div>
                      )
                    )}
                  </div>

                  {/* Calendar Days */}
                  <div className="p-2 bg-white">
                    {(() => {
                      const monthStart = startOfMonth(selectedMonth);
                      const monthEnd = endOfMonth(selectedMonth);
                      const startDay = monthStart.getDay();
                      const daysInMonth = monthEnd.getDate();

                      const weeks = [];
                      let days = [];

                      // Add empty cells for days before the month starts
                      for (let i = 0; i < startDay; i++) {
                        const prevMonthDay = new Date(monthStart);
                        prevMonthDay.setDate(
                          prevMonthDay.getDate() - (startDay - i)
                        );
                        days.push(
                          <div key={`prev-${i}`} className="p-1">
                            <CustomDayContent
                              date={prevMonthDay}
                              displayMonth={selectedMonth}
                            />
                          </div>
                        );
                      }

                      // Add days of the month
                      for (let day = 1; day <= daysInMonth; day++) {
                        const date = new Date(
                          selectedMonth.getFullYear(),
                          selectedMonth.getMonth(),
                          day
                        );
                        days.push(
                          <div key={day} className="p-1">
                            <CustomDayContent
                              date={date}
                              displayMonth={selectedMonth}
                            />
                          </div>
                        );

                        if (days.length === 7) {
                          weeks.push(
                            <div
                              key={`week-${weeks.length}`}
                              className="grid grid-cols-7"
                            >
                              {days}
                            </div>
                          );
                          days = [];
                        }
                      }

                      // Add remaining days from next month
                      if (days.length > 0) {
                        let nextMonthDay = 1;
                        while (days.length < 7) {
                          const nextDate = new Date(
                            selectedMonth.getFullYear(),
                            selectedMonth.getMonth() + 1,
                            nextMonthDay
                          );
                          days.push(
                            <div key={`next-${nextMonthDay}`} className="p-1">
                              <CustomDayContent
                                date={nextDate}
                                displayMonth={selectedMonth}
                              />
                            </div>
                          );
                          nextMonthDay++;
                        }
                        weeks.push(
                          <div
                            key={`week-${weeks.length}`}
                            className="grid grid-cols-7"
                          >
                            {days}
                          </div>
                        );
                      }

                      return weeks;
                    })()}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-100 border border-green-200"></div>
                    <span className="text-sm">Available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200"></div>
                    <span className="text-sm">Has Jobs</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-200"></div>
                    <span className="text-sm">At Capacity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-100 border border-red-200"></div>
                    <span className="text-sm">Unavailable</span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Day Details */}
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedDate
                ? format(selectedDate, "EEEE, dd MMM yyyy")
                : "Select a Date"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDate ? (
              selectedDayData ? (
                <div className="space-y-4">
                  {/* Status */}
                  <div className="flex items-center gap-2">
                    {selectedStatusType === "unavailable" ? (
                      <>
                        <XCircle className="h-5 w-5 text-red-500" />
                        <span className="font-medium text-red-600">
                          Unavailable
                        </span>
                      </>
                    ) : selectedStatusType === "atCapacity" ? (
                      <>
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                        <span className="font-medium text-yellow-600">
                          At Full Capacity
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        <span className="font-medium text-green-600">
                          Available
                        </span>
                      </>
                    )}
                  </div>

                  {/* Unavailable Reason */}
                  {selectedDayData.isUnavailable &&
                    selectedDayData.unavailableReason && (
                      <div className="p-3 bg-red-50 rounded">
                        <p className="text-sm text-red-600">
                          Reason: {selectedDayData.unavailableReason}
                        </p>
                      </div>
                    )}

                  {/* Hours Info */}
                  {selectedDayData.totalHours && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-muted/50 rounded">
                        <p className="text-xs text-muted-foreground">
                          Booked Hours
                        </p>
                        <p className="text-lg font-bold">
                          {selectedDayData.bookedHours || 0}h
                        </p>
                      </div>
                      <div className="p-3 bg-muted/50 rounded">
                        <p className="text-xs text-muted-foreground">
                          Available Hours
                        </p>
                        <p className="text-lg font-bold">
                          {selectedDayData.availableHours || 0}h
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Job Count */}
                  <div className="p-3 bg-muted/50 rounded">
                    <p className="text-sm text-muted-foreground">
                      Jobs on this day
                    </p>
                    <p className="text-2xl font-bold">
                      {selectedDayData.jobCount || 0}
                      <span className="text-sm font-normal text-muted-foreground ml-1">
                        / {selectedDayData.maxJobs || maxJobs} max
                      </span>
                    </p>
                    {/* Progress bar */}
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          selectedDayData.jobCount >=
                          (selectedDayData.maxJobs || maxJobs)
                            ? "bg-yellow-500"
                            : "bg-blue-500"
                        }`}
                        style={{
                          width: `${Math.min(
                            ((selectedDayData.jobCount || 0) /
                              (selectedDayData.maxJobs || maxJobs)) *
                              100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Jobs List */}
                  {selectedDayData.jobs?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium mb-2">
                        Scheduled Jobs:
                      </p>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedDayData.jobs.map((job, idx) => (
                          <div
                            key={job.jobId || idx}
                            className="p-2 bg-muted/50 rounded cursor-pointer hover:bg-muted transition-colors"
                            onClick={() =>
                              navigate(`/karigar-management/jobs/${job.jobId}`)
                            }
                          >
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm">
                                {job.jobNumber}
                              </p>
                              {job.priority && (
                                <Badge
                                  variant={
                                    job.priority === "URGENT"
                                      ? "destructive"
                                      : "secondary"
                                  }
                                  className="text-xs"
                                >
                                  {job.priority}
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {job.title}
                            </p>
                            {job.status && (
                              <p className="text-xs text-muted-foreground capitalize mt-1">
                                Status: {job.status}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Available Slots */}
                  {selectedStatusType !== "unavailable" && (
                    <div className="p-3 bg-green-50 rounded">
                      <p className="text-sm text-green-600">
                        Available slots:{" "}
                        <span className="font-bold">
                          {selectedDayData.jobsAvailable ||
                            (selectedDayData.maxJobs || maxJobs) -
                              (selectedDayData.jobCount || 0)}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No data available for this date</p>
                </div>
              )
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Click on a date to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      {availability?.calendarSummary && (
        <Card>
          <CardHeader>
            <CardTitle>Monthly Summary</CardTitle>
            <CardDescription>
              {format(selectedMonth, "MMMM yyyy")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-green-50 rounded">
                <p className="text-3xl font-bold text-green-600">
                  {availability.calendarSummary.availableDays || 0}
                </p>
                <p className="text-sm text-muted-foreground">Available Days</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded">
                <p className="text-3xl font-bold text-blue-600">
                  {availability.calendarSummary.daysWithJobs ||
                    availability.calendarSummary.limitedDays ||
                    0}
                </p>
                <p className="text-sm text-muted-foreground">Days with Jobs</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded">
                <p className="text-3xl font-bold text-yellow-600">
                  {availability.calendarSummary.fullCapacityDays ||
                    availability.calendarSummary.fullyBookedDays ||
                    0}
                </p>
                <p className="text-sm text-muted-foreground">At Capacity</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded">
                <p className="text-3xl font-bold text-red-600">
                  {availability.calendarSummary.unavailableDays || 0}
                </p>
                <p className="text-sm text-muted-foreground">
                  Unavailable Days
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default KarigarAvailabilityPage;
